import {
  CarrotPlanMediaCategory,
  CarrotPlanMediaItem,
  CarrotPlanMediaType,
  CarrotPlanMenuItemCategory,
  ContentTier,
  DisplayStatus,
  OptionMetadata,
  CarrotPlanRecommendation
} from "types/CarrotPlanOptions"
import { Employee } from "types/Employee"
import ResponseError from "utils/ResponseError"
import { useCarrotPlanOptions } from "./useCarrotPlanOptions"
import { useMemo } from "react"
import { UnslottedStep } from "../CarrotPlanFormTypes"
import { getAutocompleteMediaItemOption, getAutocompleteTopicOption } from "../utils/autocompleteTransformations"
import { getCarrotPlanMediaReplacementForMenuItemId } from "../MediaTypes"
import { useCarrotPlanRecommendations } from "./useCarrotPlanRecommendationsQuery"

export enum FormResponsiveDisplayStatus {
  DUPLICATE_ON_SAME_PLAN = "DUPLICATE_ON_SAME_PLAN"
}

export interface CarrotPlanFormOptionMetadata extends Omit<OptionMetadata, "displayStatus"> {
  displayStatus: DisplayStatus | FormResponsiveDisplayStatus
}

export type MenuItemOptionFields = {
  defaultUrl: string
  entityId: string
}

export type TopicOptionFields = {
  navTitle?: string | undefined
  titleWithCategory?: string | undefined
  options: CarrotPlanMediaItem[]
  link?: string
  entityId: string
}

export type MediaOptionFields = {
  categoryTitle: string
  contentType: CarrotPlanMediaType
  slug?: string
  link?: string
  entryId: string
}

export type CarrotPlanFormSelectOption = {
  group?: string
  label: string
  value: string
  optionMetadata: CarrotPlanFormOptionMetadata
  tier?: ContentTier
}

export interface CarrotPlanFormOption<TOptionFields> extends CarrotPlanFormSelectOption {
  optionFields: TOptionFields
}

type UseCarrotPlanMappings = {
  mappedFormMenuItems: CarrotPlanFormOption<MenuItemOptionFields>[]
  mappedFormTopics: CarrotPlanFormOption<TopicOptionFields>[]
  recommendedSteps: UnslottedStep[] | null
  recommendationsErrorStatus?: number
  areMappingsLoading: boolean
  fetchErrors: ResponseError
}

const mapMenuItemOptionsForAutocomplete = (
  menuOptions: CarrotPlanMenuItemCategory[]
): CarrotPlanFormOption<MenuItemOptionFields>[] => {
  // Flatten the menu options so that all menu items are in a single array
  const mappedMenuItems = menuOptions?.flatMap(({ menuItems, category }) => {
    // Map over the menu items to match the necessary shape for Carotene's FormAutoComplete component
    const mappedItems = menuItems.map(({ displayText, entityId, optionMetadata, defaultUrl, ...rest }) => {
      return {
        label: displayText,
        value: entityId,
        group: category,
        optionMetadata,
        optionFields: {
          ...rest,
          entityId,
          defaultUrl
        }
      }
    })

    // Filter out any menu items that are archived, removed, or unassigned
    return mappedItems.filter(
      (item) =>
        ![DisplayStatus.ARCHIVED, DisplayStatus.REMOVE, DisplayStatus.UNASSIGNED].includes(
          item.optionMetadata.displayStatus
        )
    )
  })

  return mappedMenuItems ?? []
}

const mapSubcategoriesForAutocomplete = (
  mediaOptions: CarrotPlanMediaCategory[]
): CarrotPlanFormOption<TopicOptionFields>[] => {
  // Flatten the media options so that all media items are in a single array
  const mappedTopics = mediaOptions?.flatMap((mediaCategory) => {
    const mappedSubCategories = mediaCategory.subCategories.map(
      // Map over the subcategories to match the necessary shape for Carotene's FormAutoComplete component
      ({ categoryTitle, entityId, mediaItems, title, titleWithCategory, navTitle, optionMetadata, ...rest }) => {
        return {
          ...rest,
          label: title,
          group: categoryTitle,
          value: entityId,
          optionMetadata,
          optionFields: {
            navTitle: navTitle,
            titleWithCategory: titleWithCategory,
            options: mediaItems,
            entityId
          }
        }
      }
    )
    // Filter out any subcategories that don't have any media items to show
    return mappedSubCategories.filter((item) => item.optionMetadata.displayStatus === DisplayStatus.SHOW.toString())
  })

  return mappedTopics ?? []
}

const buildMediaItemsById = (mediaOptions: CarrotPlanMediaCategory[] | undefined): Map<string, CarrotPlanMediaItem> => {
  const map = new Map<string, CarrotPlanMediaItem>()
  mediaOptions?.forEach((category) =>
    category.subCategories.forEach((subCategory) =>
      subCategory.mediaItems.forEach((mediaItem) =>
        map.set(mediaItem.entryId, { ...mediaItem, subCategory: subCategory.title })
      )
    )
  )
  return map
}

/**
 * Builds step rows from backend recommendations in the order the engine returned them.
 * Returns unslotted steps — `slotRecommendedSteps` stamps `id` + `stepIndex` downstream.
 */
const buildRecommendedSteps = (
  recommendations: CarrotPlanRecommendation[],
  menuItems: CarrotPlanFormOption<MenuItemOptionFields>[],
  topics: CarrotPlanFormOption<TopicOptionFields>[],
  mediaItemsById: Map<string, CarrotPlanMediaItem>
): UnslottedStep[] => {
  const menuItemsById = new Map(menuItems.map((item) => [item.optionFields.entityId, item]))
  const findMediaItemTopic = (mediaItem: CarrotPlanMediaItem) =>
    topics.find((topic) => topic.label === mediaItem.subCategory && topic.group === mediaItem.categoryTitle)

  return recommendations.flatMap((rec) => {
    const baseMenuItem = menuItemsById.get(rec.menuItemId)
    if (!baseMenuItem) return []

    const stepMenuItem: CarrotPlanFormOption<MenuItemOptionFields> = {
      ...baseMenuItem,
      tier: rec.tier
    }

    const step: UnslottedStep = {
      menuItem: stepMenuItem,
      url: stepMenuItem.optionFields?.defaultUrl ?? null
    }

    if (rec.mediaItemId) {
      const mediaItem = mediaItemsById.get(rec.mediaItemId)
      if (mediaItem) {
        const topic = findMediaItemTopic(mediaItem)
        if (topic) step.topic = getAutocompleteTopicOption(stepMenuItem.value, topic, true)
        step.content = getAutocompleteMediaItemOption(mediaItem)

        // Splice the media title into the template placeholder
        // (e.g. `Read "[article title]"` → `Read "Fertility: Overview"`).
        // The substituted label is purely cosmetic; behavior detection keys off
        // menuItem.value (entityId).
        const replacement = getCarrotPlanMediaReplacementForMenuItemId(stepMenuItem.value)
        if (replacement && stepMenuItem.label.includes(replacement)) {
          step.menuItem = { ...stepMenuItem, label: stepMenuItem.label.replace(replacement, mediaItem.title) }
        }
      }
    }

    return [step]
  })
}

export const useCarrotPlanMappings = (
  employee: Employee,
  journey?: number,
  journeyStage?: number,
  enableRecommendations = false
): UseCarrotPlanMappings => {
  const { data: carrotPlanOptions, isLoading: areCarrotPlanOptionsLoading, error } = useCarrotPlanOptions(
    employee.employeeId,
    journey,
    journeyStage
  )

  const {
    data: recommendations,
    isLoading: isRecommendationsLoading,
    isError: isRecommendationsError,
    error: recommendationsError
  } = useCarrotPlanRecommendations(employee.employeeId, journey, journeyStage, enableRecommendations)

  const recommendationsErrorStatus =
    recommendationsError instanceof ResponseError ? recommendationsError.statusCode : undefined

  const mappedMenuItems = useMemo(
    () =>
      !areCarrotPlanOptionsLoading
        ? mapMenuItemOptionsForAutocomplete(carrotPlanOptions?.menuOptions)
        : ([] as CarrotPlanFormOption<MenuItemOptionFields>[]),
    [carrotPlanOptions?.menuOptions, areCarrotPlanOptionsLoading]
  )
  const mappedMediaTopics = useMemo(
    () =>
      !areCarrotPlanOptionsLoading
        ? mapSubcategoriesForAutocomplete(carrotPlanOptions?.mediaOptions)
        : ([] as CarrotPlanFormOption<TopicOptionFields>[]),
    [carrotPlanOptions?.mediaOptions, areCarrotPlanOptionsLoading]
  )

  const recommendedSteps = useMemo<UnslottedStep[] | null>(() => {
    if (!enableRecommendations || areCarrotPlanOptionsLoading) return null
    if (isRecommendationsLoading) return null
    if (isRecommendationsError || !recommendations) return []

    const mediaItemsById = buildMediaItemsById(carrotPlanOptions?.mediaOptions)

    return buildRecommendedSteps(recommendations, mappedMenuItems, mappedMediaTopics, mediaItemsById)
  }, [
    enableRecommendations,
    areCarrotPlanOptionsLoading,
    isRecommendationsLoading,
    isRecommendationsError,
    recommendations,
    carrotPlanOptions?.mediaOptions,
    mappedMenuItems,
    mappedMediaTopics
  ])

  return {
    mappedFormMenuItems: mappedMenuItems,
    mappedFormTopics: mappedMediaTopics,
    recommendedSteps,
    recommendationsErrorStatus,
    areMappingsLoading: areCarrotPlanOptionsLoading,
    fetchErrors: error
  }
}
